import { p as parseJSON, v as validateAggregationPipeline, i as isEmptyObject } from "../../../../../../../../../chunks/jsonParser.js";
import { J as JsonEncoder } from "../../../../../../../../../chunks/JsonEncoder.js";
import { l as logger } from "../../../../../../../../../chunks/logger.js";
import { g as getMongo } from "../../../../../../../../../chunks/mongo.js";
const load = async ({ params, url }) => {
  const query = url.searchParams.get("query") || "{}";
  const sort = url.searchParams.get("sort") || "{}";
  const project = url.searchParams.get("project") || "{}";
  const skip = parseInt(url.searchParams.get("skip") || "0", 10);
  const limit = parseInt(url.searchParams.get("limit") || "20", 10);
  const mode = url.searchParams.get("mode") || "query";
  const field = url.searchParams.get("field") || "";
  const computedParams = {
    query,
    sort,
    project,
    skip,
    limit,
    mode,
    field
  };
  let queryDoc;
  let parseError = null;
  try {
    queryDoc = parseJSON(query, { allowArray: true });
  } catch (err) {
    parseError = `Invalid query: ${err}`;
  }
  if (!parseError) {
    try {
      parseJSON(sort);
    } catch (err) {
      parseError = `Invalid sort: ${err}`;
    }
  }
  if (!parseError) {
    try {
      parseJSON(project);
    } catch (err) {
      parseError = `Invalid project: ${err}`;
    }
  }
  if (parseError) {
    return {
      results: Promise.resolve({
        data: [],
        error: parseError
      }),
      count: Promise.resolve({
        data: 0,
        error: null
      }),
      params: computedParams
    };
  }
  const sortDoc = parseJSON(sort);
  const projectDoc = parseJSON(project);
  const mongo = await getMongo();
  const client = mongo.getClient(params.server);
  const collection = client.db(params.database).collection(params.collection);
  if (mode === "distinct") {
    if (!field) {
      return {
        results: Promise.resolve({
          data: [],
          error: "Invalid distinct query: field name is required"
        }),
        count: Promise.resolve({
          data: 0,
          error: null
        }),
        mappings: await client.getMappings(params.database, params.collection),
        params: computedParams
      };
    }
    const resultsPromise2 = collection.distinct(field, JsonEncoder.decode(queryDoc), {
      maxTimeMS: mongo.getQueryTimeout()
    }).then((results) => ({
      data: results.map((value) => JsonEncoder.encode({ value })),
      error: null
    })).catch((err) => {
      logger.error("Error executing distinct:", err);
      return {
        data: [],
        error: `Failed to execute distinct: ${err instanceof Error ? err.message : String(err)}`
      };
    });
    const countPromise2 = resultsPromise2.then((result) => ({
      data: result.error ? 0 : result.data.length,
      error: null
    }));
    return {
      results: resultsPromise2,
      count: countPromise2,
      mappings: await client.getMappings(params.database, params.collection),
      params: computedParams
    };
  }
  if (mode === "aggregation" && Array.isArray(queryDoc)) {
    try {
      validateAggregationPipeline(queryDoc);
    } catch (err) {
      const validationError = `Invalid aggregation pipeline: ${err instanceof Error ? err.message : String(err)}`;
      return {
        results: Promise.resolve({
          data: [],
          error: validationError
        }),
        count: Promise.resolve({
          data: 0,
          error: null
        }),
        params: computedParams
      };
    }
    const pipeline = JsonEncoder.decode(queryDoc);
    const resultsPromise2 = collection.aggregate(
      [
        ...pipeline,
        ...isEmptyObject(projectDoc) ? [] : [{ $project: projectDoc }],
        ...isEmptyObject(sortDoc) ? [] : [{ $sort: sortDoc }],
        { $limit: limit },
        { $skip: skip }
      ],
      {
        maxTimeMS: mongo.getQueryTimeout()
      }
    ).map((obj) => JsonEncoder.encode(obj)).toArray().then((results) => ({
      data: results,
      error: null
    })).catch((err) => {
      logger.error("Error executing aggregation:", err);
      return {
        data: [],
        error: `Failed to execute aggregation: ${err instanceof Error ? err.message : String(err)}`
      };
    });
    const countPromise2 = collection.aggregate([...pipeline, { $count: "count" }], { maxTimeMS: mongo.getCountTimeout() }).next().then((result) => ({
      data: result?.count ?? 0,
      error: null
    })).catch((err) => {
      logger.error("Error counting documents:", err);
      return {
        data: 0,
        error: `Failed to count documents: ${err instanceof Error ? err.message : String(err)}`
      };
    });
    return {
      results: resultsPromise2,
      count: countPromise2,
      mappings: await client.getMappings(params.database, params.collection),
      params: computedParams
    };
  }
  const resultsPromise = collection.find(JsonEncoder.decode(queryDoc), { maxTimeMS: mongo.getQueryTimeout() }).project(projectDoc).sort(JsonEncoder.decode(sortDoc)).limit(limit).skip(skip).map((obj) => JsonEncoder.encode(obj)).toArray().then((results) => ({
    data: results,
    error: null
  })).catch((err) => {
    logger.error("Error fetching query results:", err);
    return {
      data: [],
      error: `Failed to fetch query results: ${err instanceof Error ? err.message : String(err)}`
    };
  });
  const countPromise = (async () => {
    try {
      if (queryDoc && Object.keys(queryDoc).length > 0) {
        return {
          data: await collection.countDocuments(JsonEncoder.decode(queryDoc), {
            maxTimeMS: mongo.getCountTimeout()
          }),
          error: null
        };
      } else {
        return {
          data: await collection.estimatedDocumentCount(),
          error: null
        };
      }
    } catch (err) {
      logger.error("Error counting documents:", err);
      return {
        data: 0,
        error: `Failed to count documents: ${err instanceof Error ? err.message : String(err)}`
      };
    }
  })();
  return {
    // Stream these promises to the client
    results: resultsPromise,
    count: countPromise,
    mappings: await client.getMappings(params.database, params.collection),
    params: computedParams
  };
};
export {
  load
};
